import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";
import "../css/returns.scss";

const returnSteps = [
  ["01", "Tell us", "Contact customer care with your order number and the pieces you would like to return."],
  ["02", "Pack with care", "Return items unworn, unwashed and with their original tags, using protective packaging."],
  ["03", "Send and retain proof", "Use the return instructions provided and keep the carrier receipt until the refund is complete."],
];

function Returns() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="returns info-page" id="main-content">
      <div className="info-shell info-shell--narrow">
        <nav className="info-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" onClick={scrollToTop}>Home</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Returns</span>
        </nav>

        <header className="info-page__header info-page__hero">
          <p className="info-page__eyebrow">Returns & refunds</p>
          <h1>Time to decide, with a clear way back.</h1>
          <p className="info-page__lead">
            Eligible online purchases can be returned within {siteConfig.shipping.returnsWindow}
            {" "}days of delivery. Here is everything you need to prepare a smooth return.
          </p>
        </header>

        <section className="returns__window" aria-label="Returns window">
          <strong>{siteConfig.shipping.returnsWindow}</strong>
          <div>
            <span>days from delivery</span>
            <p>Try your pieces carefully at home and keep all original tags attached until you are sure.</p>
          </div>
        </section>

        <section className="returns__process" aria-labelledby="returns-process-title">
          <div className="returns__section-heading">
            <p className="info-page__eyebrow">The process</p>
            <h2 id="returns-process-title">Three considered steps.</h2>
          </div>
          <ol>
            {returnSteps.map(([number, title, copy]) => (
              <li key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="returns__eligibility" aria-labelledby="returns-eligibility-title">
          <div>
            <p className="info-page__eyebrow">Return condition</p>
            <h2 id="returns-eligibility-title">Ready for a second fitting.</h2>
          </div>
          <div className="returns__lists">
            <div>
              <h3>Eligible</h3>
              <ul>
                <li>Unworn, unwashed and unaltered items</li>
                <li>Original tags and hygiene seals still attached</li>
                <li>Footwear tried on a clean, soft surface</li>
                <li>Return requested within the stated window</li>
              </ul>
            </div>
            <div>
              <h3>Not eligible</h3>
              <ul>
                <li>Items marked final sale, where permitted</li>
                <li>Personalised or altered pieces</li>
                <li>Worn, washed, damaged or fragranced items</li>
                <li>Items without required hygiene protection</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="info-faq returns__faq" aria-labelledby="returns-faq-title">
          <div>
            <p className="info-page__eyebrow">Good to know</p>
            <h2 id="returns-faq-title">Returns, without guesswork.</h2>
          </div>
          <div className="info-faq__list">
            <details>
              <summary>When will I receive my refund?</summary>
              <p>After the return arrives, allow time for inspection. Approved refunds are sent to the original payment method; your provider may then need additional processing time.</p>
            </details>
            <details>
              <summary>Can I exchange an item?</summary>
              <p>The fastest route is usually to return the original item and place a new order, so the preferred size is not held up.</p>
            </details>
            <details>
              <summary>What if my item arrived damaged?</summary>
              <p>Contact us promptly with your order number and clear photos. Please wait for guidance before sending the item back.</p>
            </details>
            <details>
              <summary>Are original delivery costs refunded?</summary>
              <p>Mandatory rights are always respected. Beyond those rights, upgraded or express delivery charges are generally not refundable.</p>
            </details>
          </div>
        </section>

        <aside className="info-page__contact-card">
          <p className="info-page__eyebrow">Start a return</p>
          <h2>Have your order number ready.</h2>
          <p>Email <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a> or send the team a message.</p>
          <Link className="info-button" to="/contact" onClick={scrollToTop}>Contact customer care</Link>
        </aside>
      </div>
    </main>
  );
}

export default Returns;
